// A tab opened before a deploy keeps running the build it loaded. The site moves between
// pages without loading a document, and the files of earlier builds stay on the CDN, so
// nothing ever fails in such a tab and nothing ever loads the new build (code/chunk-reload.ts
// acts only when a file is gone). The owner then deploys, looks at the tab, and sees the old
// site (2026-10-28).
//
// This asks the server for index.html, shortly after the site loaded and then every few
// minutes while the tab is visible, and compares the entry file it names with the one this
// tab is running. When they differ a newer build is live, and the router loads the site again
// onto the next page asked for (router/index.ts), so an open tab is on the new build within a
// navigation. On the dev server both name /src/main.ts, so nothing happens there.

const FIRST_CHECK_MS = 10 * 1000;
const CHECK_EVERY_MS = 2 * 60 * 1000;
// index.html carries one module script, the build's entry file (a hashed name under assets/).
const ENTRY_RE = /<script[^>]*type="module"[^>]*\ssrc="([^"]+)"/;

let newer = false;
let lastCheck = 0;
let started = false;

/** The path of a script source, so the running one and a named one compare alike. */
function pathOf(src: string): string {
    try {
        return new URL(src, window.location.href).pathname;
    } catch {
        return src;
    }
}

/** The entry file this tab runs: the document's module script. Null when there is none. */
function runningEntry(): string | null {
    const script = document.querySelector<HTMLScriptElement>('script[type="module"][src]');
    const src = script && script.getAttribute("src");
    return src ? pathOf(src) : null;
}

async function check(): Promise<void> {
    if (newer || document.visibilityState === "hidden") return;
    const entry = runningEntry();
    if (!entry) return;
    const now = Date.now();
    if (now - lastCheck < CHECK_EVERY_MS) return;
    lastCheck = now;
    try {
        const res = await fetch("/index.html", { cache: "no-store", credentials: "omit" });
        if (!res.ok) return;
        const named = (await res.text()).match(ENTRY_RE);
        if (named && pathOf(named[1]) !== entry) newer = true;
    } catch {
        // offline or blocked: asked again at the next interval
    }
}

/** Whether a build newer than the one this tab runs is live. Answered from the last check. */
export function newBuildIsLive(): boolean {
    return newer;
}

/** Starts the checks: once soon after the site loaded, then every CHECK_EVERY_MS while the tab is visible. */
export function startNewBuildWatch(): void {
    if (started || typeof window === "undefined") return;
    started = true;
    window.setTimeout(() => {
        void check();
        window.setInterval(() => void check(), CHECK_EVERY_MS);
    }, FIRST_CHECK_MS);
    document.addEventListener("visibilitychange", () => {
        if (document.visibilityState === "visible") void check();
    });
}
